import React, { useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { Plus, Edit2 } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import UpdateAssignmentModal from './UpdateAssignmentModal';
import { Section, Facts, Fact, HistoryToggle, HistoryTable, SectionEmpty, formatDate, formatPeriod } from './EmployeeDetailParts';

const AssignmentCard = ({ employeeId, companyId, currentAssignment, history, currentContract, onUpdate }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

  const handleCreateClick = () => {
    if (!currentContract) {
      toast.error('Ett anställningskontrakt måste finnas innan tjänst kan läggas till');
      return;
    }
    setModalMode('create');
    setShowUpdateModal(true);
  };

  const handleEditClick = () => {
    setModalMode('edit');
    setShowUpdateModal(true);
  };

  const formatDate = (date) => {
    return moment(date).format('DD MMM YYYY');
  };

  const handleUpdateSuccess = () => {
    setShowUpdateModal(false);
    if (onUpdate) onUpdate();
  };

  const managerName = (a) => (a.manager_firstname || a.manager_lastname)
    ? [a.manager_firstname, a.manager_lastname].filter(Boolean).join(' ')
    : null;

  return (
    <>
      <Section
        id="assignment-heading"
        title="Placering"
        meta={currentAssignment ? `Gäller från ${formatDate(currentAssignment.valid_from)}` : 'Ingen placering registrerad'}
        actions={currentAssignment && (
          <>
            <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" onClick={handleEditClick} aria-label="Redigera placering">
              <Edit2 />
              Ändra
            </button>
            <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={handleCreateClick} aria-label="Skapa ny placering för anställd">
              <Plus />
              Ny placering
            </button>
          </>
        )}
      >
        {currentAssignment ? (
          <>
            <Facts>
              <Fact label="Position">
                {currentAssignment.position}
                {currentAssignment.position_level && <span className="text-muted"> · {currentAssignment.position_level}</span>}
              </Fact>
              <Fact label="Avdelning">{currentAssignment.department}</Fact>
              <Fact label="Kostnadsställe">{currentAssignment.cost_center}</Fact>
              <Fact label="Chef" hint={!currentAssignment.manager_employee_id ? 'Utan chef godkänns tidrapporter automatiskt.' : undefined}>
                {managerName(currentAssignment) || 'Ingen chef'}
              </Fact>
              <Fact label="Grad">{currentAssignment.grade}</Fact>
            </Facts>

            <HistoryToggle
              open={showHistory}
              onToggle={() => setShowHistory(!showHistory)}
              count={history && history.length > 1 ? history.length - 1 : 0}
              noun="placeringshistorik"
            />
            <Collapse in={showHistory}>
              <div>
                {history && history.length > 1 && (
                  <HistoryTable
                    columns={['Period', 'Position', 'Avdelning', 'Chef']}
                    rows={history}
                    rowKey="assign_id"
                    render={(a) => [
                      formatPeriod(a),
                      a.position_level ? `${a.position || '–'} · ${a.position_level}` : (a.position || '–'),
                      a.department || '–',
                      managerName(a) || '–',
                    ]}
                  />
                )}
              </div>
            </Collapse>
          </>
        ) : (
          <SectionEmpty
            action={
              <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={handleCreateClick} aria-label="Lägg till placering för anställd">
                <Plus />
                Lägg till placering
              </button>
            }
          >
            Position, avdelning och chef. Chefen är den som godkänner tidrapporter.
          </SectionEmpty>
        )}
      </Section>

      <UpdateAssignmentModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        employeeId={employeeId}
        companyId={companyId}
        currentAssignment={modalMode === 'edit' ? currentAssignment : null}
        mode={modalMode}
        onSuccess={handleUpdateSuccess}
      />
    </>
  );
};

export default AssignmentCard;
